import React, { useState, useEffect } from 'react';
import { Layout, Calendar as CalendarIcon, Clock, Settings, User, Moon, Sun, Grid } from 'lucide-react';
import { View, Note, Task } from './types';
import Dashboard from './components/Dashboard';
import PersianCalendar from './components/PersianCalendar';
import StudyScheduler from './components/StudyScheduler';
import Pomodoro from './components/Pomodoro';
import SettingsPage from './components/Settings';
import Tools from './components/Tools';
import Onboarding from './components/Onboarding';
import { formatPersianDateLong, PERSIAN_WEEK_DAYS } from './services/utils';

const App = () => {
  const [currentView, setCurrentView] = useState<View>(View.DASHBOARD);
  
  // Persistence Initialization
  const [darkMode, setDarkMode] = useState(() => {
    try {
      return localStorage.getItem('darkMode') === 'true';
    } catch { return false; }
  });

  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(() => {
    try {
      return localStorage.getItem('golance_onboarding_completed') === 'true';
    } catch { return false; }
  });

  const [userName, setUserName] = useState(() => {
    try {
      return localStorage.getItem('user_name') || 'دانش‌آموز';
    } catch { return 'دانش‌آموز'; }
  });

  const [deadline, setDeadline] = useState<string | null>(() => {
    try {
      const saved = localStorage.getItem('deadline');
      return saved ? JSON.parse(saved) : new Date(new Date().setDate(new Date().getDate() + 15)).toISOString().split('T')[0];
    } catch { return null; }
  });

  const [notes, setNotes] = useState<Note[]>(() => {
    try {
      const saved = localStorage.getItem('notes');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  const [tasks, setTasks] = useState<Task[]>(() => {
    try {
      const saved = localStorage.getItem('tasks');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  // Effects to save changes
  useEffect(() => localStorage.setItem('darkMode', String(darkMode)), [darkMode]);
  useEffect(() => localStorage.setItem('deadline', JSON.stringify(deadline)), [deadline]);
  useEffect(() => localStorage.setItem('notes', JSON.stringify(notes)), [notes]);
  useEffect(() => localStorage.setItem('tasks', JSON.stringify(tasks)), [tasks]);
  useEffect(() => localStorage.setItem('golance_onboarding_completed', String(hasCompletedOnboarding)), [hasCompletedOnboarding]);
  useEffect(() => localStorage.setItem('user_name', userName), [userName]);

  // Dark Mode Class Effect
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Handlers
  const handleAddNote = (date: string, content: string) => {
    const newNote: Note = {
      id: Date.now().toString(),
      date,
      content
    };
    setNotes([...notes, newNote]);
  };

  const handleDeleteNote = (id: string) => {
    setNotes(notes.filter(n => n.id !== id));
  };

  const handleOnboardingComplete = (name: string) => {
    setUserName(name);
    setHasCompletedOnboarding(true);
  };

  const today = new Date();
  const dayName = PERSIAN_WEEK_DAYS[(today.getDay() + 1) % 7];

  const navItems = [
    { id: View.DASHBOARD, icon: Layout, label: 'داشبورد' },
    { id: View.CALENDAR, icon: CalendarIcon, label: 'تقویم' },
    { id: View.SCHEDULER, icon: Clock, label: 'برنامه' },
    { id: View.TOOLS, icon: Grid, label: 'ابزارها' },
    { id: View.SETTINGS, icon: Settings, label: 'تنظیمات' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex text-slate-800 dark:text-slate-100 font-sans transition-colors duration-300" dir="rtl">
      
      {!hasCompletedOnboarding && (
        <Onboarding onComplete={handleOnboardingComplete} />
      )}

      {/* Desktop Sidebar Navigation */}
      <aside className="hidden lg:flex w-64 bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 flex-col flex-shrink-0 sticky top-0 h-screen z-20 transition-all duration-300">
        <div className="p-6 flex items-center justify-start gap-3">
          <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
            گ
          </div>
          <span className="font-bold text-lg text-slate-900 dark:text-white tracking-tight">گلنس</span>
        </div>

        <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto custom-scrollbar">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setCurrentView(item.id)}
              className={`
                w-full flex items-center justify-start gap-3 p-3 rounded-xl transition-all duration-200
                ${currentView === item.id 
                  ? 'bg-primary-50 dark:bg-slate-800 text-primary-600 dark:text-primary-400 shadow-sm' 
                  : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'}
              `}
              title={item.label}
            >
              <item.icon size={22} strokeWidth={2} />
              <span className="font-medium">{item.label}</span>
            </button>
          ))}
        </nav>

        {/* Pomodoro Slot for Desktop Docking */}
        <div id="sidebar-pomodoro-slot" className="px-2 mb-2"></div>

        {/* Footer / User Profile */}
        <div className="p-4 border-t border-slate-100 dark:border-slate-800">
          <div className="flex items-center justify-start gap-3 p-2 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer">
            <div className="w-8 h-8 bg-slate-200 dark:bg-slate-700 rounded-full flex items-center justify-center text-slate-500 dark:text-slate-300 flex-shrink-0">
              <User size={16} />
            </div>
            <div className="text-right overflow-hidden">
              <p className="text-sm font-bold text-slate-700 dark:text-slate-200 truncate">{userName}</p>
              <p className="text-xs text-slate-400 dark:text-slate-500 truncate">طرح حرفه‌ای</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col h-screen overflow-hidden relative pb-20 lg:pb-0">
        {/* Top Header */}
        <header className="h-16 border-b border-slate-100 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md px-4 md:px-8 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-3">
             {/* Mobile Logo */}
             <div className="lg:hidden w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                گ
              </div>
            <h1 className="text-lg md:text-xl font-bold text-slate-800 dark:text-white truncate">
              {currentView === View.DASHBOARD && 'داشبورد'}
              {currentView === View.CALENDAR && 'تقویم'}
              {currentView === View.SCHEDULER && 'برنامه‌ریزی'}
              {currentView === View.SETTINGS && 'تنظیمات'}
              {currentView === View.TOOLS && 'جعبه ابزار'}
              <span className="hidden md:inline">
                 {currentView === View.DASHBOARD && ' وضعیت'}
                 {currentView === View.CALENDAR && ' شمسی'}
                 {currentView === View.SCHEDULER && ' تحصیلی'}
                 {currentView === View.TOOLS && ' کاربردی'}
              </span>
            </h1>
          </div>
          
          <div className="flex items-center gap-3 md:gap-4">
             <div className="text-xs md:text-sm text-slate-400 font-medium hidden sm:block">
               {dayName}، {formatPersianDateLong(today)}
            </div>
            <div className="h-6 w-px bg-slate-200 dark:bg-slate-700 hidden sm:block"></div>
            <button 
              onClick={() => setDarkMode(!darkMode)}
              className="p-2 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
            >
              {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>
          </div>
        </header>

        {/* Content Body */}
        <div className="flex-1 overflow-auto p-4 md:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto min-h-full">
            {currentView === View.DASHBOARD && (
              <Dashboard 
                deadline={deadline} 
                setDeadline={setDeadline} 
                taskCount={tasks.length} 
              />
            )}
            
            {currentView === View.CALENDAR && (
              <PersianCalendar 
                notes={notes} 
                onAddNote={handleAddNote}
                onDeleteNote={handleDeleteNote}
              />
            )}
            
            {currentView === View.SCHEDULER && (
              <StudyScheduler 
                tasks={tasks} 
                setTasks={setTasks} 
                daysUntilDeadline={deadline ? Math.max(0, Math.ceil((new Date(deadline).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))) : 7}
              />
            )}

            {currentView === View.TOOLS && (
              <Tools 
                tasks={tasks}
                notes={notes}
                deadline={deadline}
              />
            )}

            {currentView === View.SETTINGS && (
              <SettingsPage 
                darkMode={darkMode}
                setDarkMode={setDarkMode}
                tasks={tasks}
                notes={notes}
                deadline={deadline}
                setTasks={setTasks}
                setNotes={setNotes}
                setDeadline={setDeadline}
              />
            )}
          </div>
        </div>

        {/* Global Widget Mounter - Persistent State */}
        {/* Pass props to help it decide where to mount */}
        <Pomodoro mobileToolsActive={currentView === View.TOOLS} />
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 z-40 flex justify-around items-center px-2 pb-safe shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
        {navItems.map((item) => (
          <button
            key={item.id}
            onClick={() => setCurrentView(item.id)}
            className={`
              flex flex-col items-center justify-center gap-1 p-3 flex-1 transition-all duration-200
              ${currentView === item.id 
                ? 'text-primary-600 dark:text-primary-400' 
                : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'}
            `}
          >
            <item.icon size={24} strokeWidth={currentView === item.id ? 2.5 : 2} />
            <span className="text-[10px] font-medium">{item.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
};

export default App;